
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Modelo de destinatario de notificaciones.
 */
export interface NotificacionDestinatario {
  id: number;
  nombre: string;
  correo: string;
  recibir_vehiculos: boolean;
  recibir_conductores: boolean;
  notificar_vencidos: boolean;
  activo: boolean;
  fecha_creacion?: Date | string;
  fecha_actualizacion?: Date | string;
}

/**
 * Datos para crear un destinatario.
 */
export interface CrearNotificacionDestinatario {
  nombre: string;
  correo: string;
  recibir_vehiculos: boolean;
  recibir_conductores: boolean;
  notificar_vencidos: boolean;
  activo: boolean;
}

/**
 * Respuesta estándar del backend.
 */
export interface RespuestaApi<T> {
  codigo: number;
  mensaje: string;
  datos?: T;
}

/**
 * Respuesta de listado paginado.
 */
export interface RespuestaListadoDestinatarios {
  codigo: number;
  mensaje: string;
  datos: Destinatario[];
  paginacion: {
    total: number;
    pagina: number;
    limite: number;
    totalPaginas: number;
  };
}

export interface Destinatario {
  id: number;
  nombre: string;
  correo: string;
  recibir_vehiculos: boolean;
  recibir_conductores: boolean;
  notificar_vencidos: boolean;
  activo: boolean;
  fecha_creacion: string;
  fecha_actualizacion: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificacionDestinatarioService {

  private readonly apiUrl =
    `${environment.apiUrl}/api/notificacion-destinatarios`;

  constructor(private http: HttpClient) {}

  /**
   * Listar destinatarios con filtros y paginación.
   */
  listar(
    pagina: number = 1,
    limite: number = 10,
    busqueda: string = '',
    activo?: boolean
  ): Observable<RespuestaListadoDestinatarios> {

    let params = new HttpParams()
      .set('pagina', pagina)
      .set('limite', limite);

    if (busqueda.trim()) {
      params = params.set('busqueda', busqueda.trim());
    }

    if (activo !== undefined) {
      params = params.set('activo', activo);
    }

    return this.http.get<RespuestaListadoDestinatarios>(
      this.apiUrl,
      { params }
    );
  }

  /**
   * Consultar destinatario por ID.
   */
  obtenerPorId(
    id: number
  ): Observable<RespuestaApi<NotificacionDestinatario>> {

    return this.http.get<RespuestaApi<NotificacionDestinatario>>(
      `${this.apiUrl}/${id}`
    );
  }

  /**
   * Crear destinatario.
   */
  crear(
    datos: CrearNotificacionDestinatario
  ): Observable<RespuestaApi<NotificacionDestinatario>> {

    return this.http.post<RespuestaApi<NotificacionDestinatario>>(
      this.apiUrl,
      datos
    );
  }

  /**
   * Actualizar destinatario.
   */
  actualizar(
    id: number,
    datos: Partial<CrearNotificacionDestinatario>
  ): Observable<RespuestaApi<NotificacionDestinatario>> {

    return this.http.put<RespuestaApi<NotificacionDestinatario>>(
      `${this.apiUrl}/${id}`,
      datos
    );
  }

  /**
   * Activar o desactivar destinatario.
   */
  cambiarEstado(
    id: number,
    activo: boolean
  ): Observable<RespuestaApi<NotificacionDestinatario>> {

    return this.http.patch<RespuestaApi<NotificacionDestinatario>>(
      `${this.apiUrl}/${id}/estado`,
      { activo }
    );
  }

  /**
   * Eliminar destinatario.
   */
  eliminar(
    id: number
  ): Observable<RespuestaApi<null>> {

    return this.http.delete<RespuestaApi<null>>(
      `${this.apiUrl}/${id}`
    );
  }
}
